import { useEffect, useState } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ShoppingBag, Navigation, ExternalLink } from "lucide-react";
import { scrollToId } from "./Navbar";

const GALLERY = [
  "https://images.unsplash.com/photo-1696592783645-33e966c4c274?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHxjYXJnbyUyMHZhbiUyMG5pZ2h0JTIwdHJhbnNwb3J0fGVufDB8fHx8MTc5MTQ5NjIxNnww&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1639416943955-c1281e3c2da3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwyfHxjYXJnbyUyMHZhbiUyMG5pZ2h0JTIwdHJhbnNwb3J0fGVufDB8fHx8MTc5MTQ5NjIxNnww&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1783971573755-3d8a94233cb9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHxf...&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1535655685871-dc8158ff167e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHw0fHxjYXJnbyUyMHZhbiUyMGR1c2t8ZW58MHx8fHwxNzkxNTAwOTg1fDA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1784643486110-5acaabb20c1c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w8NjY2NjV8MHwxfHNlYXJjaHw0fHxkZWxpdmVyeSUyMHZhbiUyMGhpZ2h3YXklMjBkdXNrJTIwbGlnaHQlMjB0cmFpbHN8ZW58MHx8fHwxNzkxNTAxNDU0fDA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1771207895109-0c09d6cd3f9b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHw0fHx3aGl0ZSUyMGNhcmdvJTIwdmFuJTIwZHVzayUyMGRlbGl2ZXJ5fGVufDB8fHx8MTc5MTQ5NjIyMXww&ixlib=rb-4.1.0&q=85",
];

const LINES = ["Deine Fracht.", "Dein Auto.", "Deine Werkstatt."];

export const Hero = () => {
  const { scrollY } = useScroll();
  const yImg = useTransform(scrollY, [0, 700], [0, 130]);
  const yText = useTransform(scrollY, [0, 700], [0, -60]);
  const [slide, setSlide] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setSlide((s) => (s + 1) % GALLERY.length), 4500);
    return () => clearInterval(t);
  }, []);

  return (
    <section id="hero" className="relative min-h-screen flex items-center overflow-hidden noise-overlay grid-lines">
      <div className="absolute inset-0 bg-gradient-to-br from-[#0B0F17] via-[#0B0F17] to-[#101a30]" />
      <div className="absolute -top-40 -right-40 w-[620px] h-[620px] rounded-full bg-[#1E6FD9]/15 blur-[140px]" />
      <div className="absolute bottom-0 left-0 w-[420px] h-[420px] rounded-full bg-[#29B6F6]/10 blur-[120px]" />

      <div className="relative z-10 max-w-7xl mx-auto px-5 lg:px-8 pt-32 pb-20 grid lg:grid-cols-12 gap-14 items-center w-full">
        <motion.div style={{ y: yText }} className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="inline-flex items-center gap-2.5 rounded-full border border-cyan-400/25 bg-cyan-400/5 px-4 py-1.5 mb-8"
          >
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse-dot" />
            <span className="text-xs font-mono-tech tracking-[0.2em] text-cyan-300 uppercase">
              Dispo live · Karlsruhe
            </span>
          </motion.div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-black tracking-tight leading-[1.05] text-slate-50">
            {LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${i === 2 ? "text-gradient-blue" : ""}`}
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.25 + i * 0.14, ease: [0.16, 1, 0.3, 1] }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75 }}
            className="mt-7 text-base md:text-lg text-slate-400 max-w-xl leading-relaxed"
          >
            M-Line Solutions bringt deine Ware von A nach B — vom Express-Paket bis zur Palettenfracht, mit
            Live-Preisberechnung: <span className="text-cyan-300 font-mono-tech">Grundpreis 50 € (inkl. 15 km) + 0,80–1,50 €/km netto</span>, Geschenkkarten
            für Werkstatt-Services im Shop und Service auf höchstem Niveau. Dein{" "}
            <span className="text-cyan-300">11. Transport ist kostenlos.</span>
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-booking-cta"
              onClick={() => scrollToId("fahrt")}
              className="group inline-flex items-center gap-2.5 rounded-full bg-gradient-to-r from-[#1E6FD9] to-[#29B6F6] px-7 py-3.5 text-sm font-semibold text-white shadow-[0_16px_40px_-12px_rgba(30,111,217,0.7)] transition-transform duration-300 hover:scale-[1.04] active:scale-[0.98]"
            >
              <Navigation size={16} />
              Transport jetzt buchen
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-shop-cta"
              onClick={() => scrollToId("shop")}
              className="inline-flex items-center gap-2.5 rounded-full border border-white/15 bg-white/5 px-7 py-3.5 text-sm font-semibold text-slate-200 hover:border-cyan-400/40 hover:text-cyan-300 transition-colors duration-300"
            >
              <ShoppingBag size={16} />
              Zum Shop
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.1 }}
            className="mt-12 flex gap-10 font-mono-tech"
          >
            {[
              ["ab 50 €", "Grundpreis inkl. 15 km"],
              ["11.", "Transport gratis"],
              ["21", "Transporter"],
            ].map(([v, l]) => (
              <div key={l}>
                <div className="text-2xl font-bold text-slate-50">{v}</div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 mt-1">{l}</div>
              </div>
            ))}
          </motion.div>
          <motion.a
            data-testid="hero-partner-badge"
            href="https://www.carboncleangermany.de"
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.25 }}
            className="mt-8 inline-flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-2.5 hover:border-cyan-400/40 transition-colors group"
          >
            <img
              src="/partner-carbonclean.jpg"
              alt="CarbonCleanGermany — Motor Carbon Clean"
              className="w-11 h-11 rounded-xl object-cover"
            />
            <div>
              <div className="text-[9px] uppercase tracking-[0.25em] text-slate-500 font-mono-tech">
                In direkter Partnerschaft mit
              </div>
              <div className="text-xs font-semibold text-slate-200 group-hover:text-cyan-300 transition-colors flex items-center gap-1.5">
                CarbonCleanGermany <ExternalLink size={11} className="text-cyan-400" />
              </div>
            </div>
          </motion.a>
        </motion.div>

        <motion.div
          style={{ y: yImg }}
          initial={{ opacity: 0, x: 60 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 1.1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-5 relative"
        >
          <div className="relative rounded-[2rem] overflow-hidden glow-border" data-testid="hero-gallery">
            <div className="relative w-full h-[460px] lg:h-[540px]">
              <AnimatePresence mode="sync">
                <motion.img
                  key={slide}
                  src={GALLERY[slide]}
                  alt="M-Line Flotte im Einsatz"
                  initial={{ opacity: 0, scale: 1.06 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </AnimatePresence>
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F17]/85 via-transparent to-transparent pointer-events-none" />
            <div className="absolute top-5 right-5 flex gap-1.5 z-10">
              {GALLERY.map((_, i) => (
                <button
                  key={i}
                  data-testid={`hero-gallery-dot-${i}`}
                  onClick={() => setSlide(i)}
                  aria-label={`Bild ${i + 1}`}
                  className={`h-1.5 rounded-full transition-[width,background-color] duration-500 ${
                    i === slide ? "w-6 bg-cyan-400" : "w-1.5 bg-white/30 hover:bg-white/60"
                  }`}
                />
              ))}
            </div>
            <div className="absolute bottom-5 left-5 right-5 glass-panel rounded-2xl p-4 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-cyan-400 font-mono-tech">M-Line Flotte</div>
                <div className="text-sm font-semibold text-slate-100 mt-0.5">21 Transporter im Einsatz · bis 4,20 m Ladefläche</div>
              </div>
              <div className="text-right">
                <div className="font-mono-tech text-lg font-bold text-gradient-blue">ab 50 € + 0,80–1,50 €/km netto</div>
              </div>
            </div>
          </div>
          <div className="absolute -top-4 -left-4 w-24 h-24 border-t-2 border-l-2 border-cyan-400/40 rounded-tl-[2rem]" />
          <div className="absolute -bottom-4 -right-4 w-24 h-24 border-b-2 border-r-2 border-[#1E6FD9]/40 rounded-br-[2rem]" />
        </motion.div>
      </div>
    </section>
  );
};
